import { call, put, takeEvery, select } from 'redux-saga/effects';
import {
  defineKqlQuery,
  fetchCategories,
  fetchSubmission,
  searchSubmissions,
  createSubmission,
  updateSubmission,
  submitSubmission,
  deleteSubmission,
  generateKey,
} from '@kineticdata/react';
import {
  C,
  addToastAlert,
  cloneSubmission,
  // postWebApi,
  selectServicesKappSlug,
} from '@kineticdata/bundle-common';
import { actions, types } from '../modules/settingsService';
import { List } from 'immutable';

import { getDefaultContent } from '../../components/shared/content_manager/defaults';

/**
 * Feches all services, retrieving all draft, pending, and active versions.
 * Only a small subset of fields are retrieved to prevent storing the full
 * Content JSON string in state for every record.
 */
export function* fetchServicesSaga() {
  const { submissions, error } = yield call(searchSubmissions, {
    kapp: C.KAPP_SLUG_DATASTORE,
    form: C.FORM_SLUG_SERVICE,
    limit: 1000,
    search: {
      q: defineKqlQuery()
        .in(`values[${C.FIELD_STATUS}]`, 'statuses')
        .end()({
        statuses: ['Draft', 'Pending', 'Active'],
      }),
      include: [
        'details',
        ...C.SERVICE_PRIMARY_FIELDS.map(f => `values[${f}]`),
        'form',
      ],
    },
  });

  if (error) {
    yield put(actions.fetchServicesFailure(error));
  } else {
    yield put(actions.fetchServicesSuccess(submissions));
  }
}

/**
 * Fetches the selected service, retrieving all draft, pending, and active
 * versions. The latest active version gets set as the current service, and
 * the full list is used to update the services state with the latest data for
 * this service id.
 */
export function* fetchServiceSaga({ payload }) {
  const searcher = defineKqlQuery()
    .in(`values[${C.FIELD_STATUS}]`, 'statuses')
    .equals(`values[${C.FIELD_SERVICE_ID}]`, 'id');

  const { submissions, error } = yield call(searchSubmissions, {
    kapp: C.KAPP_SLUG_DATASTORE,
    form: C.FORM_SLUG_SERVICE,
    limit: 1000,
    search: {
      q: searcher.end()({
        statuses: ['Draft', 'Pending', 'Active'],
        id: payload,
      }),
      include: [
        'details',
        'values',
        'children',
        'children.values',
        'children.details',
        'form',
      ],
    },
  });

  if (error) {
    yield put(actions.fetchServiceFailure(error));
  } else {
    // Sort returned services by most recently updated
    const sortedSubmissions = List(submissions)
      .sortBy(p => p.updatedAt)
      .reverse();
    // Get current service as the most recently updated Active one, or just
    // the most recently updated one if there are no Active ones
    const currentService =
      sortedSubmissions.find(s => s.values[C.FIELD_STATUS] === 'Active') ||
      sortedSubmissions.first();

    if (!!currentService) {
      // Modify list of services to only have the necessary values to prevent
      // the redux store from having a ton of data due to all the Content
      const list = sortedSubmissions.map(submission => ({
        ...submission,
        values: C.SERVICE_PRIMARY_FIELDS.reduce(
          (vals, field) => ({ ...vals, [field]: submission.values[field] }),
          {},
        ),
      }));

      yield put(
        actions.fetchServiceSuccess({
          serviceId: payload,
          record: currentService,
          list,
        }),
      );
    } else {
      yield put(
        actions.fetchServiceFailure({
          statusCode: 404,
          message: `Unable to locate the ${payload} service`,
        }),
      );
    }
  }
}

/**
 * Fetches the selected service revision.
 */
export function* fetchServiceRevisionSaga({
  payload: { revisionId, serviceId },
}) {
  const { submission, error } = yield call(fetchSubmission, {
    id: revisionId,
    include: 'details,values,children,children.values,children.details,form',
  });

  if (error) {
    yield put(actions.fetchServiceRevisionFailure(error));
  } else if (
    !!serviceId &&
    submission.values[C.FIELD_SERVICE_ID] !== serviceId
  ) {
    // If the Service Id of the revision doesn't match the provided Service
    // Id, the URL must have been manipulated.
    yield put(
      actions.fetchServiceRevisionFailure({
        message: `The selected revision doesn't belong to your current service.`,
      }),
    );
  } else {
    // If no current service set in state or the revision submission is for a
    // different service, fetch the correct current service
    const currentService = yield select(state => state.settingsService.current);
    if (
      !currentService ||
      currentService.values[C.FIELD_SERVICE_ID] !==
        submission.values[C.FIELD_SERVICE_ID]
    ) {
      yield put(actions.fetchServiceRequest(serviceId));
    }

    yield put(actions.fetchServiceRevisionSuccess(submission));
  }
}

/**
 * Creates a new service in Draft status
 */
export function* createServiceSaga({ payload = {} }) {
  const { success, failure, complete, values = {} } = payload;

  // Generate a key to use as the Service Id
  const serviceId = generateKey(9);
  // TODO verify that key isn't already used, or maybe use submission id as key (set after cretiong)

  const { submission, error } = yield call(createSubmission, {
    kappSlug: C.KAPP_SLUG_DATASTORE,
    formSlug: C.FORM_SLUG_SERVICE,
    values: {
      Name: `New Service ${serviceId}`,
      Type: 'Internal',
      ...values,
      [C.FIELD_SERVICE_ID]: serviceId,
      Status: 'Draft',
      Content: getDefaultContent(C.FORM_SLUG_SERVICE),
    },
    include: 'details,values,form',
    completed: false,
  });

  if (error && typeof failure === 'function') {
    failure(error);
  } else if (submission) {
    if (typeof success === 'function') {
      success(submission);
    }

    // Call sync service categories webAPI to create a new category
    // const servicesKappSlug = yield select(selectServicesKappSlug);
    // yield call(postWebApi, {
    //   slug: C.WEBAPI_SYNC_SERVICE_CATEGORIES,
    //   kappSlug: servicesKappSlug,
    //   data: [
    //     {
    //       name: submission.values['Name'],
    //       id: serviceId,
    //     },
    //   ],
    // });

    // On success, fetch the service so its submissions are included in the list
    yield put(actions.fetchServiceRequest(serviceId));
  }
  if (typeof complete === 'function') {
    complete();
  }

  yield put(actions.serviceActionComplete());
}

/**
 * Creates a service revision by cloning an existing service into Draft status
 */
export function* cloneServiceSaga({ payload = {} }) {
  const { success, failure, complete, submissionId } = payload;

  const { submission, error } = yield call(cloneSubmission, {
    id: submissionId,
    values: { Status: 'Draft' },
    include: 'details,values,form',
    completed: false,
  });

  if (error && typeof failure === 'function') {
    failure(error);
  } else if (submission && typeof success === 'function') {
    success(submission);

    // On success, fetch the service so its submissions are included in the list
    yield put(
      actions.fetchServiceRequest(submission.values[C.FIELD_SERVICE_ID]),
    );
  }
  if (typeof complete === 'function') {
    complete();
  }

  yield put(actions.serviceActionComplete());
}

/**
 * Updates a service to an Expired status. If clone flag is true, first
 * creates a clone of the service into draft status.
 */
export function* expireServiceSaga({ payload = {} }) {
  const {
    success,
    failure,
    complete,
    submissionId,
    clone = false,
    refetchItem = false,
  } = payload;

  // If clone flag is true, clone the submission into draft state
  if (clone) {
    const { error } = yield call(cloneSubmission, {
      id: submissionId,
      values: { Status: 'Draft' },
      completed: false,
    });

    if (error) {
      if (typeof failure === 'function') {
        failure(error);
      }
      return;
    }
  }

  const { submission, error } = yield call(updateSubmission, {
    id: submissionId,
    values: { Status: 'Expired', 'Locked By': '' },
    include: 'details,values,form',
  });

  if (error && typeof failure === 'function') {
    failure(error);
  } else if (submission) {
    if (typeof success === 'function') {
      success(submission);
    }

    // If the current service still exists, refetch the service
    if (refetchItem) {
      yield put(
        actions.fetchServiceRequest(submission.values[C.FIELD_SERVICE_ID]),
      );
    }
    // Otherwise fetch services list to get updated list of all data
    else {
      yield put(actions.fetchServicesRequest());

      // Call sync service categories webAPI to delete the category if the service no longer exists
      // const servicesKappSlug = yield select(selectServicesKappSlug);
      // yield call(postWebApi, {
      //   slug: C.WEBAPI_SYNC_SERVICE_CATEGORIES,
      //   kappSlug: servicesKappSlug,
      //   data: [
      //     {
      //       id: submission.values[C.FIELD_SERVICE_ID],
      //     },
      //   ],
      // });
    }
  }
  if (typeof complete === 'function') {
    complete();
  }

  yield put(actions.serviceActionComplete());
}

/**
 * Updates a portoflio revision, keeping the status as Draft
 */
export function* saveServiceSaga({ payload = {} }) {
  const {
    success,
    failure,
    complete,
    submissionId,
    updatedAt,
    values,
  } = payload;

  // Retrieve the current submission to get its values and verify that the user
  // had the latest version and isn't overwriting anyones changes.
  const { submission: existingSubmission, error: existingError } = yield call(
    fetchSubmission,
    {
      id: submissionId,
      include: 'details,values,form',
    },
  );

  if (existingError) {
    failure(existingError);
  } else if (existingSubmission.updatedAt !== updatedAt) {
    failure({
      message:
        'This revision has been updated since you last loaded it. Please refresh your revision.',
    });
  } else {
    const profile = yield select(state => state.app.profile);

    const { submission, error } = yield call(updateSubmission, {
      id: submissionId,
      values: { ...values, Status: 'Draft', 'Locked By': profile.username },
      include: 'details,values,children,children.values,children.details,form',
    });

    if (error && typeof failure === 'function') {
      failure(error);
    } else if (submission) {
      if (typeof success === 'function') {
        success(submission);
      }

      // If we updated the current revision, update the revision data
      const revision = yield select(state => state.settingsService.revision);
      if (revision && revision.id === submissionId) {
        yield put(actions.fetchServiceRevisionSuccess(submission));
      }
      // Otherwise fetch the updated service so its submissions are included in the list
      else {
        yield put(
          actions.fetchServiceRequest(submission.values[C.FIELD_SERVICE_ID]),
        );
      }
    }
  }
  if (typeof complete === 'function') {
    complete();
  }

  yield put(actions.serviceActionComplete());
}

/**
 * Submits a service revision, changing the status to Pending
 */
export function* submitServiceSaga({ payload = {} }) {
  const { success, failure, complete, submissionId, updatedAt } = payload;

  // Retrieve the current submission to get its values and verify that the user
  // is submitting the latest version.
  const { submission: existingSubmission, error: existingError } = yield call(
    fetchSubmission,
    {
      id: submissionId,
      include: 'details,values,form',
    },
  );

  if (existingError) {
    failure(existingError);
  } else if (existingSubmission.updatedAt !== updatedAt) {
    failure({
      message:
        'This revision has been updated since you last loaded it. Please refresh your revision, verify the data, and submit again.',
    });
  } else {
    const { submission, error } = yield call(submitSubmission, {
      id: submissionId,
      values: {
        ...existingSubmission.values,
        Status: 'Pending',
        'Locked By': '',
      },
      page: 'Page 1',
      include: 'details,values,children,children.values,children.details,form',
    });

    if (error && typeof failure === 'function') {
      failure(error);
    } else if (submission) {
      if (typeof success === 'function') {
        success(submission);
      }

      // If we updated the current revision, refetch the revision data
      const revision = yield select(state => state.settingsService.revision);
      if (revision && revision.id === submissionId) {
        yield put(actions.fetchServiceRevisionSuccess(submission));
      }
      // Otherwise fetch the updated service so its submissions are included in the list
      else {
        yield put(
          actions.fetchServiceRequest(submission.values[C.FIELD_SERVICE_ID]),
        );
      }
    }
  }
  if (typeof complete === 'function') {
    complete();
  }

  yield put(actions.serviceActionComplete());
}

/**
 * Locks or unlocks a service revision
 */
export function* lockServiceSaga({ payload = {} }) {
  const { success, failure, complete, submissionId, unlock = false } = payload;

  const profile = yield select(state => state.app.profile);

  const { submission, error } = yield call(updateSubmission, {
    id: submissionId,
    values: { 'Locked By': unlock ? '' : profile.username },
    include: 'details,values,children,children.values,children.details,form',
  });

  if (error && typeof failure === 'function') {
    failure(error);
  } else if (submission) {
    if (typeof success === 'function') {
      success(submission);
    }

    // If we updated the current revision, update the revision data
    const revision = yield select(state => state.settingsService.revision);
    if (revision && revision.id === submissionId) {
      yield put(actions.fetchServiceRevisionSuccess(submission));
    }
    // Otherwise fetch the updated service so its submissions are included in the list
    else {
      yield put(
        actions.fetchServiceRequest(submission.values[C.FIELD_SERVICE_ID]),
      );
    }
  }
  if (typeof complete === 'function') {
    complete();
  }

  yield put(actions.serviceActionComplete());
}

/**
 * Deletes a service revision
 */
export function* deleteServiceSaga({ payload = {} }) {
  const {
    success,
    failure,
    complete,
    submissionId,
    cancel = false,
    refetchItem = false,
  } = payload;

  // If cancel flag is false, then we can just delete the submissions
  if (!cancel) {
    const { submission, error } = yield call(deleteSubmission, {
      id: submissionId,
      include: 'details,values,form',
    });

    if (error && typeof failure === 'function') {
      failure(error);
    }
    if (!error) {
      if (typeof success === 'function') {
        success();
      }

      // If the current service still exists, refetch the service
      if (refetchItem) {
        yield put(
          actions.fetchServiceRequest(submission.values[C.FIELD_SERVICE_ID]),
        );
      }
      // Otherwise fetch services list to get updated list of all data
      else {
        yield put(actions.fetchServicesRequest());

        // Call sync service categories webAPI to delete the category if the service no longer exists
        // const servicesKappSlug = yield select(selectServicesKappSlug);
        // yield call(postWebApi, {
        //   slug: C.WEBAPI_SYNC_SERVICE_CATEGORIES,
        //   kappSlug: servicesKappSlug,
        //   data: [
        //     {
        //       id: submission.values[C.FIELD_SERVICE_ID],
        //     },
        //   ],
        // });
      }
    }
  } else {
    // Retrieve the current submission to get its values so we can update it to
    // a Cancelled status
    const { submission: existingSubmission, error: existingError } = yield call(
      fetchSubmission,
      {
        id: submissionId,
        include: 'details,values,form',
      },
    );

    if (existingError) {
      failure(existingError);
    } else if (existingSubmission.values[C.FIELD_STATUS] !== 'Draft') {
      failure({
        message:
          'This revision status has been updated since you last loaded it. Please refresh your revision.',
      });
    } else {
      const { submission, error } = yield call(submitSubmission, {
        id: submissionId,
        values: {
          ...existingSubmission.values,
          Status: 'Cancelled',
          'Locked By': '',
        },
        page: 'Page 1',
        include: 'details,values,form',
      });

      if (error && typeof failure === 'function') {
        failure(error);
      }
      if (!error) {
        if (typeof success === 'function') {
          success();
        }

        // If the current service still exists, refetch the service
        if (refetchItem) {
          yield put(
            actions.fetchServiceRequest(submission.values[C.FIELD_SERVICE_ID]),
          );
        }
        // Otherwise fetch services list to get updated list of all data
        else {
          yield put(actions.fetchServicesRequest());

          // Call sync service categories webAPI to delete the category if the service no longer exists
          // const servicesKappSlug = yield select(selectServicesKappSlug);
          // yield call(postWebApi, {
          //   slug: C.WEBAPI_SYNC_SERVICE_CATEGORIES,
          //   kappSlug: servicesKappSlug,
          //   data: [
          //     {
          //       id: submission.values[C.FIELD_SERVICE_ID],
          //     },
          //   ],
          // });
        }
      }
    }
  }

  if (typeof complete === 'function') {
    complete();
  }

  yield put(actions.serviceActionComplete());
}

export function* fetchCategoriesSaga() {
  const kappSlug = yield select(selectServicesKappSlug);

  const { categories } = yield call(fetchCategories, {
    kappSlug,
    include: 'attributesMap[Service Category]',
  });

  if (categories) {
    yield put(actions.fetchCategoriesComplete(categories));
  }
}

export function syncCategoriesSaga({ payload }) {
  if (Array.isArray(payload) && payload.length > 0) {
    // const servicesKappSlug = yield select(selectServicesKappSlug);
    // yield call(postWebApi, {
    //   slug: C.WEBAPI_SYNC_SERVICE_CATEGORIES,
    //   kappSlug: servicesKappSlug,
    //   data: payload,
    // });

    // addToastAlert({
    //   severity: 'info',
    //   title: 'Sync Started',
    //   message:
    //     'A background task has been started to synchronize services and categories.',
    //   duration: 5000,
    // });
    addToastAlert({
      severity: 'danger',
      title: 'Sync Disabled',
      message: 'Synchronizing services and categories is disabled.',
      duration: 5000,
    });
  }
}

export function* watchSettingsService() {
  yield takeEvery(
    [types.FETCH_SERVICES_REQUEST, types.FETCH_SERVICES_BY_ID_REQUEST],
    fetchServicesSaga,
  );
  yield takeEvery(types.FETCH_SERVICE_REQUEST, fetchServiceSaga);
  yield takeEvery(
    types.FETCH_SERVICE_REVISION_REQUEST,
    fetchServiceRevisionSaga,
  );
  yield takeEvery(types.CREATE_SERVICE_REQUEST, createServiceSaga);
  yield takeEvery(types.CLONE_SERVICE_REQUEST, cloneServiceSaga);
  yield takeEvery(types.EXPIRE_SERVICE_REQUEST, expireServiceSaga);
  yield takeEvery(types.SAVE_SERVICE_REVISION_REQUEST, saveServiceSaga);
  yield takeEvery(types.LOCK_SERVICE_REVISION_REQUEST, lockServiceSaga);
  yield takeEvery(types.DELETE_SERVICE_REVISION_REQUEST, deleteServiceSaga);
  yield takeEvery(types.SUBMIT_SERVICE_REVISION_REQUEST, submitServiceSaga);
  yield takeEvery(types.FETCH_CATEGORIES_REQUEST, fetchCategoriesSaga);
  yield takeEvery(types.SYNC_CATEGORIES_REQUEST, syncCategoriesSaga);
}
